import { useNavigate } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { toast } from 'react-toastify';
import { Box, Button, TextField } from '@mui/material';
import styles from './ContactUsForm.module.scss';
import { Country } from '../order/models';
import { ContactUsFormViewModel } from './contact-us-form-view.model';
import { IContactUsDto } from './contact-us-form-dto.interface';
import { contactUs, getLinkToContactSuccess, htmlId } from './fixcenter-support.service';
import classNames from 'classnames';
import globalStyles from '../../global-styles.module.scss';
import { SearchableCountryField } from '../../shared/country-select/SearchableCountryField';
import { createContactUsValidationSchema } from './ContactUsForm.validation';

export interface IContactUsFormInput {
    isFromContactUs: boolean;
}

const rowSx = {
    display: 'grid',
    alignItems: 'center',
    columnGap: 3,
    mb: 2,
    mt: 2,
};

const ContactUsForm: React.FC<IContactUsFormInput> = ({ isFromContactUs }) => {
    const { t } = useTranslation('translation');

    const navigate = useNavigate();
    const showError = (message: string) => toast.error(message);

    const validationSchema = useMemo(() => createContactUsValidationSchema(t), [t]);

    const getModelDto = (values: ContactUsFormViewModel): IContactUsDto => {
        const dto: IContactUsDto = {
            name: values.name,
            company: values.company,
            email: values.email,
            phone: values.phone,
            postalCode: values.postalCode,
            country: values.country,
            message: values.message,
            jsonMessages: JSON.stringify(''),
            title:
                'Fix center (' +
                (isFromContactUs ? t('CONTACT_US').toUpperCase() : t('header.VISIT US').toUpperCase()) +
                ')',
            files: [],
        };
        return dto;
    };

    const send = (values: ContactUsFormViewModel): void | Promise<any> => {
        contactUs(getModelDto(values))
            .then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    navigate(getLinkToContactSuccess());
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                }
            })
            .catch(() => showError(t('commonErrors.SENDING_MESSAGE')));
    };

    const formik = useFormik<ContactUsFormViewModel>({
        validateOnMount: true,
        initialValues: new ContactUsFormViewModel(),
        validationSchema,
        onSubmit: (values) => {
            send(values);
        },
    });

    const displayFillRequiredData = () => {
        formik.validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
                showError(t('register.fillAllRequiredData'));
            }
        });
    };

    const errorText = (field: keyof ContactUsFormViewModel): string | undefined => {
        const error = formik.errors[field];
        return formik.touched[field] && error ? (error as string) : undefined;
    };

    const handleCountrySelect = (country: Country) => {
        formik.setFieldValue('country', country.name).then(() => {
            formik.setFieldTouched('country', true, false);
        });
    };

    const handleCountryBlur = () => {
        formik.setFieldTouched('country', true);
    };

    return (
        <div className={styles.form_container}>
            <Box component="form" className={styles.form} onSubmit={formik.handleSubmit} autoComplete="off">
                <div className={styles.form_content}>
                    <div className={styles.content}>
                        <label className={styles.main_header_text}>
                            {isFromContactUs ? t('CONTACT_US') : t('header.VISIT US')}
                        </label>
                        {!isFromContactUs && (
                            <Box sx={{ mb: 2 }}>
                                <p>{t('VISIT_US_DEF.GET_IN_TOUCH_WITH_FIXCENTER')}</p>
                            </Box>
                        )}

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                id={`${htmlId}-name-input`}
                                name="name"
                                label={t('NAME')}
                                variant="outlined"
                                size="small"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('name'))}
                                helperText={errorText('name')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                id={`${htmlId}-company-name-input`}
                                name="company"
                                label={t('COMPANY')}
                                variant="outlined"
                                size="small"
                                value={formik.values.company}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('company'))}
                                helperText={errorText('company')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                id={`${htmlId}-email-input`}
                                name="email"
                                type="email"
                                label={t('EMAIL')}
                                variant="outlined"
                                size="small"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('email'))}
                                helperText={errorText('email')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                id={`${htmlId}-phone-input`}
                                name="phone"
                                label={t('PHONE')}
                                variant="outlined"
                                size="small"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('phone'))}
                                helperText={errorText('phone')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                id={`${htmlId}-zip-input`}
                                name="postalCode"
                                label={t('ZIP')}
                                variant="outlined"
                                size="small"
                                value={formik.values.postalCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('postalCode'))}
                                helperText={errorText('postalCode')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <SearchableCountryField
                                id={`${htmlId}-country-code-select`}
                                name="country"
                                label={t('COUNTRY')}
                                value={formik.values.country}
                                getOptionValue={(option) => option.name}
                                onSelect={handleCountrySelect}
                                onBlur={handleCountryBlur}
                                error={Boolean(errorText('country'))}
                                helperText={errorText('country')}
                            />
                        </Box>

                        <Box sx={rowSx}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                multiline
                                rows={4}
                                id={`${htmlId}-message-textarea`}
                                name="message"
                                label={t('MESSAGE')}
                                variant="outlined"
                                size="small"
                                value={formik.values.message}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(errorText('message'))}
                                helperText={errorText('message')}
                            />
                        </Box>

                        <div className={styles.button_content}>
                            <Button
                                className={styles.send_btn}
                                variant="contained"
                                type="submit"
                                id={`${htmlId}-submit-btn`}
                                onClick={displayFillRequiredData}
                                sx={{
                                    backgroundColor: '#821818',
                                    color: '#fff',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#821818',
                                        boxShadow: 'none',
                                    },
                                    '&:active': {
                                        boxShadow: 'none',
                                    },
                                    '&:focus': {
                                        outline: 'none',
                                    },
                                }}
                                disableRipple
                            >
                                {t('SEND')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Box>
            {!isFromContactUs && (
                <div className={globalStyles.flex_center_item}>
                    <div
                        className={classNames(globalStyles.fixcenter_support_visit_us_sprites, globalStyles.img_3)}
                    ></div>
                </div>
            )}
        </div>
    );
};

export default ContactUsForm;
